import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const TYPES = ["View", "Procedure", "Function", "Module Query", "Report", "Agent", "Others"];
const PRODUCTS = ["Focus 6", "Focus 7", "Focus 9/X", "Apps", "Others"];
const COMPLEXITIES = ["High", "Medium", "Low"];

const emptyForm = {
  title: "",
  purpose: "",
  type: "Procedure",
  product: "Focus 9/X",
  project: "",
  sql_content: "",
  notes: "",
  complexity: "Medium",
};

export default function QueryFormDialog({ open, onOpenChange, editingQuery, onSuccess }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [projects, setProjects] = useState([]);
  const [projectSuggestionsOpen, setProjectSuggestionsOpen] = useState(false);
  const fileInputRef = useRef(null);
  const projectInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (editingQuery) {
        setForm({
          title: editingQuery.title || "",
          purpose: editingQuery.purpose || "",
          type: editingQuery.type || "Procedure",
          product: editingQuery.product || "Focus 9/X",
          project: editingQuery.project || "",
          sql_content: editingQuery.sql_content || "",
          notes: editingQuery.notes || "",
          complexity: editingQuery.complexity || "Medium",
        });
      } else {
        setForm(emptyForm);
      }
      setUploadedFileName("");
      // Load existing project names for autocomplete
      api.get("/queries/meta/projects").then((r) => setProjects(r.data?.projects || [])).catch(() => {});
    }
  }, [open, editingQuery]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      update("sql_content", evt.target.result);
      setUploadedFileName(file.name);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsText(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.sql_content.trim()) {
      toast.error("Title and SQL content are required");
      return;
    }
    setSubmitting(true);
    try {
      let resp;
      if (editingQuery) {
        resp = await api.put(`/queries/${editingQuery.id}`, form);
        toast.success("Query updated (new version saved)");
      } else {
        resp = await api.post("/queries", form);
        toast.success("Query created");
      }
      onSuccess(resp.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="query-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">
            {editingQuery ? "Edit Query" : "Add SQL Query"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="e.g., Get Customer Outstanding by Branch"
              required
              data-testid="form-title-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Purpose</Label>
            <Textarea
              value={form.purpose}
              onChange={(e) => update("purpose", e.target.value)}
              placeholder="What does this query do? When is it used?"
              rows={2}
              data-testid="form-purpose-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Type *</Label>
              <Select value={form.type} onValueChange={(v) => update("type", v)}>
                <SelectTrigger data-testid="form-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Product *</Label>
              <Select value={form.product} onValueChange={(v) => update("product", v)}>
                <SelectTrigger data-testid="form-product-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Complexity *</Label>
              <Select value={form.complexity} onValueChange={(v) => update("complexity", v)}>
                <SelectTrigger data-testid="form-complexity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPLEXITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5 relative">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Project</Label>
            <div className="relative">
              <FolderKanban className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                ref={projectInputRef}
                value={form.project}
                onChange={(e) => { update("project", e.target.value); setProjectSuggestionsOpen(true); }}
                onFocus={() => setProjectSuggestionsOpen(true)}
                onBlur={() => setTimeout(() => setProjectSuggestionsOpen(false), 150)}
                placeholder="e.g., ABC Manufacturing, Client X Migration"
                className="pl-10"
                data-testid="form-project-input"
                autoComplete="off"
              />
            </div>
            {projectSuggestionsOpen && (() => {
              const q = form.project.toLowerCase().trim();
              const matches = projects.filter((p) => !q || p.toLowerCase().includes(q));
              if (matches.length === 0) return null;
              return (
                <div
                  className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto"
                  data-testid="project-suggestions"
                >
                  {matches.slice(0, 10).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); update("project", p); setProjectSuggestionsOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors border-b border-slate-100 last:border-0"
                      data-testid={`project-suggestion-${p}`}
                    >
                      <FolderKanban className="inline w-3.5 h-3.5 mr-2 text-slate-400" />
                      {p}
                    </button>
                  ))}
                </div>
              );
            })()}
            <p className="text-[11px] text-slate-500">Start typing to see existing projects — picking one avoids typos.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">SQL Query *</Label>
            <Tabs defaultValue="paste" className="w-full">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="paste" data-testid="form-tab-paste">
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Paste Text
                </TabsTrigger>
                <TabsTrigger value="upload" data-testid="form-tab-upload">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload File
                </TabsTrigger>
              </TabsList>
              <TabsContent value="paste" className="mt-2">
                <Textarea
                  value={form.sql_content}
                  onChange={(e) => update("sql_content", e.target.value)}
                  placeholder="SELECT * FROM ..."
                  rows={10}
                  className="font-mono text-xs bg-slate-900 text-slate-100 border-slate-700 focus-visible:ring-blue-500"
                  data-testid="form-sql-textarea"
                />
              </TabsContent>
              <TabsContent value="upload" className="mt-2">
                <div className="border-2 border-dashed border-slate-300 rounded-md p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".sql,.txt"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="form-file-input"
                  />
                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="form-file-upload-button"
                  >
                    Choose .sql or .txt file
                  </Button>
                  {uploadedFileName && (
                    <div className="mt-2 text-xs text-slate-600 font-mono">{uploadedFileName} loaded</div>
                  )}
                  {form.sql_content && (
                    <div className="mt-3 text-left">
                      <div className="text-xs text-slate-500 mb-1">Preview:</div>
                      <pre className="sql-code-block max-h-32 overflow-auto text-xs">{form.sql_content.slice(0, 500)}{form.sql_content.length > 500 ? "..." : ""}</pre>
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Any caveats, parameters, dependencies…"
              rows={3}
              data-testid="form-notes-input"
            />
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="form-cancel-button">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="form-submit-button"
            >
              {submitting ? "Saving…" : editingQuery ? "Save New Version" : "Create Query"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
